import React from 'react'
import find from 'lodash/find'
import { Col, Row } from 'react-bootstrap'
import { useFormikContext } from 'formik'
import type { PivotFilterOperatorEnum } from '@ztechapps/common.contract'
import type { DateType } from '@ztechapps/common.utils'
import type { SelectOption, SelectProps } from '@ztechapps/webui.input'
import type { PivotFilterFormValues } from '../pivot/pivotFilterForm.utils'
import { PivotFieldTypeEnum } from '@ztechapps/common.contract'
import { isPivotRangeOperator } from '@ztechapps/common.utils'
import { DateTimeInput } from '@ztechapps/webui.date'
import { Field } from '@ztechapps/webui.form'
import { Select, TextInput } from '@ztechapps/webui.input'
import { ShowIf } from '@ztechapps/webui.util'

/** Props for the single-value select of the filter dialog. */
export type PivotFilterSelectProps = {
  options: SelectOption[]
  value?: string
  onChange?: (value: string) => void
} & Omit<SelectProps, 'options' | 'value' | 'onChange'>

/** A react-select holding one option's value, so a Formik field stores the plain string. */
export const PivotFilterSelect = ({ options, value, onChange, ...rest }: PivotFilterSelectProps) => (
  <Select
    {...rest}
    options={options}
    value={find(options, { value }) ?? null}
    onChange={(option) => onChange?.((option as SelectOption).value)}
    isClearable={false}
  />
)

/** Props for the day input of the filter dialog. */
export type PivotDayInputProps = {
  id?: string
  value?: string
  min?: string
  max?: string
  onChange?: (day: string) => void
}

/** A date input holding a local day (`YYYY-MM-DD`), or '' once cleared. */
export const PivotDayInput = ({ id, value, min, max, onChange }: PivotDayInputProps) => (
  <DateTimeInput
    id={id}
    type="date"
    value={value ?? ''}
    min={min}
    max={max}
    // a date input reports `YYYY-MM-DDTHH:mm`, or '' once cleared; the filter keeps the day alone
    onChange={(next: DateType) => onChange?.(String(next ?? '').slice(0, 10))}
  />
)

/** The form section holding the operands of an operator. */
export enum PivotFilterSectionEnum {
  condition = 'condition',
  value = 'value',
}

/** The two operand slots of a section. */
export enum PivotOperandEnum {
  value1 = 'value1',
  value2 = 'value2',
}

/** Props for the operand inputs of an operator. */
export type PivotOperandFieldsProps = {
  /** The form section holding `value1` and `value2`. */
  section: PivotFilterSectionEnum
  operator: PivotFilterOperatorEnum
  valueType: PivotFieldTypeEnum
}

/** One input for an operator, two (From and To) for between and not between — a day picker for a date. */
export const PivotOperandFields = ({ section, operator, valueType }: PivotOperandFieldsProps) => {
  const { values } = useFormikContext<PivotFilterFormValues>()
  const isRange = isPivotRangeOperator(operator)
  const isDate = valueType === PivotFieldTypeEnum.Date
  const operand = (name: PivotOperandEnum, label: string) => (
    <Field
      name={`${section}.${name}`}
      label={label}
      component={isDate ? PivotDayInput : TextInput}
      {...(isDate
        ? { onChange: true, min: name === PivotOperandEnum.value2 ? values[section].value1 : undefined }
        : { placeholder: valueType === PivotFieldTypeEnum.Number ? 'Number' : 'Text', autoFocus: false })}
    />
  )
  return (
    <Row className="g-2">
      <Col xs={isRange ? 6 : 12}>{operand(PivotOperandEnum.value1, isRange ? 'From' : 'Value')}</Col>
      <ShowIf condition={isRange}>
        <Col xs={6}>{operand(PivotOperandEnum.value2, 'To')}</Col>
      </ShowIf>
    </Row>
  )
}
